/** @prettier */
import qBankApi, {
    makeRequest,
    Product,
    API_ROOT,
    API_ROOT_V2,
    addParamsToUrl,
    PageParams,
} from "../services/qbankApi";
import { apiImage, partitionImages } from "../util/imageUtils";

// Right now these all have the same shape
export type Subject = Product;
export type Category = Product;
export type Subcategory = Product;

export type QuestionImageData = {
    image: string | File;
    fileName: string;
} & ({ type: "fetched_image"; id: string } | { type: "new_image" });
export type RemovedImageData = QuestionImageData & {
    id: string;
    imageType: "STEM" | "EXPLANATION";
};

export type AnswerChoice = { choice: string; correct: boolean };
export type ReinforcementQuestion = { answer: string; question: string };
type User = { username: string; uuid: string };

export type Comment = {
    createdByUuid: string;
    createdDate: string;
    modifiedDate: string;
    read: boolean;
    text: string;
    uuid: string;
};

export type Question = {
    answerChoices: AnswerChoice[];
    // API actually represents unassigned differently, but we transform it to undefined
    author?: User & { fullName: string };
    category: Category;
    commentList: Comment[];
    correctAnswerExplanation: string;
    // creator: User;
    incorrectAnswerExplanation: string;
    keyConcept: string;
    learningObjective: string;
    product: Product;
    references: string[];
    reinforcementQuestions: ReinforcementQuestion[];
    status: string;
    stem: string;
    subcategory: Subcategory;
    subject: Subject;
    uuid: string;
    versionInfo?: {
        versionNum: number;
        latestVersionNum: number;
    };
};

type MakeOptional<T, K extends keyof T> = Partial<Pick<T, K>> & Omit<T, K>;
export type DraftQuestion = MakeOptional<
    Question,
    "uuid" | "product" | "subject" | "category" | "subcategory"
>;

// Map from API representation to UI representation.
// API represents an unassigned author with `{"uuid": null, "username": "UNASSIGNED", "fullName": null}`
// UI uses undefined
function buildQuestion(rawQuestionData: any): Question {
    return {
        ...rawQuestionData,
        author:
            (rawQuestionData.author?.uuid && rawQuestionData.author) ??
            undefined,
    };
}

export function getQuestion(questionId: string) {
    return makeRequest(
        "GET",
        API_ROOT_V2 + "questions/" + questionId,
    ).then((response) => response.json().then(buildQuestion));
}

export async function getQuestions(url: string) {
    const rawQuestions: {
        content: any[];
        totalPages: number;
    } = await makeRequest("GET", url).then((response) => response.json());

    return {
        ...rawQuestions,
        content: rawQuestions.content.map(buildQuestion),
    };
}

export const getUserQuestions = (productId: string, params: PageParams) =>
    getQuestions(
        addParamsToUrl(
            `${API_ROOT_V2}questions/products/${productId}/writer`,
            params,
        ),
    );

export const getProductQuestions = (
    productId: string,
    params: Record<string, string | number | undefined>,
) =>
    getQuestions(
        addParamsToUrl(
            `${API_ROOT_V2}questions/products/${productId}/auditor`,
            params,
        ),
    );

export function updateQuestion(questionId: string, question: DraftQuestion) {
    return makeRequest(
        "PUT",
        API_ROOT_V2 + "questions/" + questionId,
        question,
    ).then((response) => response.json() as Promise<Question>);
}

export function postQuestion(question: DraftQuestion) {
    return makeRequest("POST", API_ROOT_V2 + "questions", question).then(
        (response) => response.json() as Promise<Question>,
    );
}

/** Saves a question, either creating it if it's new or else updating an existing question */
export async function saveQuestion(
    /** undefined if it's a new question to be created */
    questionUuid: string | undefined,
    question: DraftQuestion,
    images: {
        removed: RemovedImageData[];
        stem: QuestionImageData[];
        explanation: QuestionImageData[];
    },
) {
    const { uuid: newUuid } = await (questionUuid
        ? updateQuestion(questionUuid, question)
        : postQuestion(question));

    await Promise.all(
        images.removed.map(({ id, imageType }) =>
            qBankApi.deleteQuestionImage(newUuid, id, imageType),
        ),
    );
    if (images.stem.length > 0 || images.explanation.length > 0) {
        const newQuestionStemImages = images.stem.filter(
            ({ type }) => type === "new_image",
        );

        const newQuestionExplanationImages = images.explanation.filter(
            ({ type }) => type === "new_image",
        );

        await qBankApi.postQuestionImages(
            newUuid,
            newQuestionStemImages,
            newQuestionExplanationImages,
        );
    }
    return newUuid;
}

export async function deleteQuestion(questionId: string) {
    const url = `${API_ROOT}questions/${questionId}`;
    await makeRequest("DELETE", url);
}

export async function getImageThumbnails(questionId: string) {
    const url = `${API_ROOT}questionImages/${questionId}/thumbnails`;
    const res = (await (await makeRequest("GET", url)).json()) as apiImage[];
    return partitionImages(res);
}
